import { useNavigate, useSearchParams } from 'react-router-dom';
import useAuth from '~/hooks/useAuth';
import { usePayoutDetailsV2Flag } from '~/hooks/usePayoutDetailsV2Flag';
import { PagePolicies } from '~/utils/roles';

import { useBetaChoicesStore } from './betaChoicesStore';
import { type BetaPageId, betaPages } from './betaPages';
import type { BetaNotice } from './components/BetaTitleChip';

export const useAvailableBetaPages = (): Record<BetaPageId, boolean> => {
  const { hasPermission } = useAuth();
  const { isPayoutDetailsV2Enabled } = usePayoutDetailsV2Flag();
  const hasBetaAccess = hasPermission(PagePolicies.BetaPages);

  return { users: hasBetaAccess, accounts: hasBetaAccess, payoutDetails: isPayoutDetailsV2Enabled };
};

export const useChoosePath = () => {
  const available = useAvailableBetaPages();
  const optedIn = useBetaChoicesStore((state) => state.choices.optedIn);

  return (page: BetaPageId, url: string) => {
    const { switching } = betaPages[page];
    if (switching.kind === 'in-place') return url;

    return available[page] && optedIn.includes(page) ? switching.betaPath : switching.classicPath;
  };
};

export const useShowsBeta = (page: BetaPageId) => {
  const available = useAvailableBetaPages();
  const optedIn = useBetaChoicesStore((state) => state.choices.optedIn.includes(page));

  return available[page] && optedIn;
};

export const useTryBeta = () => {
  const navigate = useNavigate();
  const tryBeta = useBetaChoicesStore((state) => state.tryBeta);

  return (page: BetaPageId) => {
    const { switching } = betaPages[page];
    tryBeta(page);
    if (switching.kind === 'route') navigate(switching.betaPath);
  };
};

const useLeaveBeta = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const switchToClassic = useBetaChoicesStore((state) => state.switchToClassic);

  return (page: BetaPageId) => {
    const { switching } = betaPages[page];
    switchToClassic(page);
    if (switching.kind === 'route') return navigate(switching.classicPath);

    return setSearchParams(
      new URLSearchParams([...searchParams].filter(([key]) => key !== switching.viewParam)),
      { replace: true },
    );
  };
};

export const useBetaNotice = (page: BetaPageId, version: 'classic' | 'beta'): BetaNotice | null => {
  const available = useAvailableBetaPages();
  const tryBeta = useTryBeta();
  const leaveBeta = useLeaveBeta();
  const dismissed = useBetaChoicesStore((state) => state.choices.dismissedOffers.includes(page));
  const dismissOffer = useBetaChoicesStore((state) => state.dismissOffer);

  const pageName = betaPages[page].name;

  if (!available[page]) return null;

  if (version === 'beta')
    return { kind: 'in-beta', pageName, onSwitchToClassic: () => leaveBeta(page) };

  if (dismissed) return null;

  return {
    kind: 'offer',
    pageName,
    onTry: () => tryBeta(page),
    onDismiss: () => dismissOffer(page),
  };
};
